// Global Utils
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';

// Global Selectors
// @ts-ignore
import { selectCurrentOrganizationId } from 'services/currentOrganizationId/selectors';
// @ts-ignore
import { selectCurrentSeasonId } from 'services/currentSeasonId/selectors';
import { selectIsAdmin, selectIsPayroll } from 'services/usersMe/selectors';

const selectLoadScaffoldDataParams = createDeepEqualSelector(
    [
        selectCurrentOrganizationId,
        selectCurrentSeasonId,
        selectIsAdmin,
        selectIsPayroll
    ],
    (
        currentOrganizationIdState: string,
        currentSeasonIdState: number,
        isAdmin,
        isPayroll
    ) => {
        let modelsByRole = {};

        if (isAdmin || isPayroll) {
            modelsByRole = {
                ...modelsByRole,
                payrollExports: {
                    model: 'PayrollExports',
                    filter: {
                        where: { deletedAt: { $exists: false } },
                        fields: [
                            'id',
                            'createdAt',
                            'createdByUserId',
                            'email',
                            'numberOfTimesheets'
                        ]
                    }
                }
            };
        }

        return {
            organizationId: currentOrganizationIdState,
            shape: {
                commodities: {
                    model: 'Commodities',
                    filter: {
                        where: { deletedAt: { $exists: false } },
                        fields: ['id', 'name']
                    }
                },
                descriptionSnippets: {
                    model: 'DescriptionSnippets',
                    filter: {
                        where: { deletedAt: { $exists: false } },
                        fields: ['id', 'schemaAttributeId', 'text']
                    }
                },
                entities: {
                    model: 'Entities',
                    filter: {
                        eager: '[assignedUsers, removals]',
                        where: {
                            seasonId: currentSeasonIdState
                        },
                        fields: [
                            'archivedAt',
                            'costCentre',
                            'createdAt',
                            'defaultCommodityId',
                            'defaultVarietyId',
                            'deletedAt',
                            'entityTypeId',
                            'fieldValue0',
                            'fieldValue1',
                            'fieldValue2',
                            'fieldValue3',
                            'fieldValue4',
                            'id',
                            'managementArea',
                            'name',
                            'numberOfTrees',
                            'orchardRpin',
                            'parentEntityId',
                            'productionSite',
                            'rowSpacing',
                            'seasonId',
                            'totalArea',
                            'updatedAt',
                            'uuid',
                            'plantingDate',
                            'removals.id',
                            'removals.date',
                            'removals.numberOfTrees',
                            'removals.totalArea',
                            'isNonProductive'
                        ]
                    }
                },
                entityTypes: {
                    model: 'EntityTypes',
                    filter: {
                        where: { deletedAt: { $exists: false } },
                        fields: [
                            'id',
                            'name',
                            'depth',
                            'hasDefaultVariety',
                            'fieldLabel0',
                            'fieldLabel1',
                            'fieldLabel2',
                            'fieldLabel3',
                            'fieldLabel4',
                            'createdAt',
                            'updatedAt',
                            'deletedAt',
                            'pluralName',
                            'isOrchard',
                            'customNameLabel'
                        ]
                    }
                },
                equipmentSets: {
                    model: 'SprayEquipmentSets',
                    filter: {
                        eager: '[calibrations, entities]',
                        where: {
                            deletedAt: { $exists: false },
                            'calibrations.deletedAt': { $exists: false },
                            'entities.seasonId': currentSeasonIdState
                        },
                        order: 'calibrations.applicationRate, calibrations.rowSpacingMin asc'
                    }
                },
                groups: {
                    model: 'Groups',
                    filter: {
                        where: { deletedAt: { $exists: false } },
                        fields: [
                            'id',
                            'name',
                            'createdAt',
                            'updatedAt',
                            'deletedAt',
                            'color'
                        ]
                    }
                },
                groupEntities: {
                    model: 'GroupEntities',
                    filter: {
                        require: {
                            'group.deletedAt': { $exists: false },
                            'entity.deletedAt': { $exists: false },
                            'entity.seasonId': currentSeasonIdState
                        }
                    }
                },
                groupPeople: {
                    model: 'GroupPeople',
                    filter: {
                        require: {
                            'group.deletedAt': { $exists: false },
                            'person.deletedAt': { $exists: false }
                        }
                    }
                },
                harvestBinSuitabilities: {
                    model: 'HarvestBinSuitabilities',
                    filter: {
                        fields: [
                            'id',
                            'name',
                            'externalId',
                            'deletedAt',
                            'orderIndex'
                        ]
                    }
                },
                harvestGrowingMethods: {
                    model: 'HarvestGrowingMethods',
                    filter: {
                        fields: [
                            'id',
                            'name',
                            'externalId',
                            'deletedAt',
                            'orderIndex'
                        ]
                    }
                },
                harvestLocations: {
                    model: 'HarvestLocations',
                    filter: {
                        fields: [
                            'id',
                            'name',
                            'externalId',
                            'deletedAt',
                            'orderIndex'
                        ]
                    }
                },
                harvestPalletTypes: {
                    model: 'HarvestPalletTypes',
                    filter: {
                        fields: [
                            'id',
                            'name',
                            'externalId',
                            'deletedAt',
                            'orderIndex'
                        ]
                    }
                },
                harvestStorageTypes: {
                    model: 'HarvestStorageTypes',
                    filter: {
                        fields: [
                            'id',
                            'name',
                            'externalId',
                            'deletedAt',
                            'orderIndex'
                        ]
                    }
                },
                harvestSubmissionProfiles: {
                    model: 'HarvestSubmissionProfiles',
                    filter: {
                        fields: [
                            'id',
                            'name',
                            'externalId',
                            'deletedAt',
                            'orderIndex'
                        ]
                    }
                },
                people: {
                    model: 'People',
                    filter: {
                        eager: 'sprayCertification',
                        where: {
                            deletedAt: { $exists: false },
                            'sprayCertifications.deletedAt': null
                        },
                        fields: [
                            'id',
                            'firstName',
                            'lastName',
                            'datacomId',
                            'email',
                            'roleIds',
                            'mobileNumber',
                            'isSubscribedToHarvestEmail',
                            'isSubscribedToHarvestSms',
                            'userId',
                            'sectorNotifications'
                        ]
                    }
                },
                pickTypes: {
                    model: 'PickTypes',
                    filter: { where: { deletedAt: { $exists: false } } }
                },
                products: {
                    model: 'SprayProducts',
                    filter: {
                        eager: 'activeIngredients'
                    }
                },
                productTypes: {
                    model: 'SprayProductTypes'
                },
                roles: {
                    model: 'Roles',
                    filter: { where: { deletedAt: { $exists: false } } }
                },
                seasons: {
                    model: 'Seasons',
                    filter: { where: { deletedAt: { $exists: false } } }
                },
                schemas: {
                    model: 'Schemas',
                    filter: {
                        eager: 'workTypes',
                        fields: [
                            'eventTypeId',
                            'groupIdIsEnabled',
                            'hasInstructions',
                            'id',
                            'isEnabled',
                            'name',
                            'workTypes.id',
                            'entityTypeId',
                            'innerEntityTypeId',
                            'category'
                        ]
                    }
                },
                schemaAttributes: {
                    model: 'SchemaAttribute',
                    filter: {
                        require: {
                            deletedAt: { $exists: false },
                            'schema.deletedAt': { $exists: false }
                        }
                    }
                },
                userEntityAssignments: {
                    model: 'UserEntityAssignments',
                    filter: {
                        require: {
                            'entity.deletedAt': { $exists: false },
                            'entity.archivedAt': { $exists: false },
                            'user.deletedAt': { $exists: false }
                        },
                        fields: ['entityId', 'jobType', 'userEmail']
                    }
                },
                varieties: {
                    model: 'Varieties',
                    filter: {
                        where: { deletedAt: { $exists: false } },
                        fields: [
                            'id',
                            'uuid',
                            'name',
                            'commodityId',
                            'code',
                            'shortName'
                        ]
                    }
                },
                waterSource: {
                    model: 'SprayWaterSource'
                },
                workTypes: {
                    model: 'WorkTypes'
                },
                unitTypes: {
                    model: 'UnitType'
                },
                equipment: {
                    model: 'Equipment'
                },
                activeIngredients: {
                    model: 'ActiveIngredient'
                },
                timesheetHolidays: {
                    model: 'TimesheetHolidays',
                    filter: {
                        eager: 'entities',
                        where: {
                            deletedAt: { $exists: false },
                            'entities.seasonId': currentSeasonIdState
                        },
                        order: 'entities.name'
                    }
                },
                organizations: {
                    model: 'Organizations',
                    filter: {
                        where: {
                            id: currentOrganizationIdState
                        }
                    }
                },
                organizationConfigs: {
                    model: 'OrganizationConfig',
                    filter: {
                        fields: [
                            'id',
                            'isMyTimesheetEnabled',
                            'isNotificationsEnabled',
                            'entityTypeCostCentreDepth',
                            'isHarvestEnabled',
                            'isRemovalsEnabled',
                            'isTimesheetImportEnabled'
                        ]
                    }
                },
                fertilizerProducts: {
                    model: 'FertilizerProducts'
                },
                ...modelsByRole
            }
        };
    }
);

export { selectLoadScaffoldDataParams };
